"use client";
import { SlidersHorizontal, X, Ruler, Layers, Palette, Trash2, Tag } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { useParams, useSearchParams, useRouter, usePathname } from 'next/navigation'
import type { ProductFilters } from '@/types'

interface Props {
  filterOptions: { sizes: string[]; materials: string[]; colours: string[]; finishes: string[] }
  totalProducts: number
  filterCounts?: Record<string, number>
}

const REAL_COLOUR_MAP: Record<string, string> = {
  Silver: '#C0C0C0',
  Chrome: '#BEC2CB',
  Gold: '#D4AF37',
  Black: '#2D2D2D',
  'Z-Black Powder Coated': '#1a1a1a',
  Galvanised: '#8A9A8A',
  Antique: '#8B7355',
  'Antique Brass': '#8B7355',
  Nickel: '#A19D94',
  Mirror: '#E8E8E8',
  'Rose Gold': '#E0BFB8',
  'Rose Gold Plated': '#E0BFB8',
  Copper: '#B87333',
  'Copper Antique': '#B87333',
  'Natural SS': '#D1D5DB'
}

export function CategoryFilters({ filterOptions, totalProducts, filterCounts }: Props) {
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()

  const getFiltersFromParams = (): ProductFilters => ({
    size: searchParams.getAll('size'),
    material: searchParams.getAll('material'),
    colour: searchParams.getAll('colour'),
    finish: searchParams.getAll('finish'),
  })

  const filters = getFiltersFromParams()
  const hasActiveFilters = Object.values(filters).some((v) => v && v.length > 0)

  const toggleFilter = (key: keyof ProductFilters, value: string) => {
    const current = searchParams.getAll(key)
    const next = new URLSearchParams(searchParams.toString())
    next.delete(key)
    if (current.includes(value)) {
      current.filter((v) => v !== value).forEach((v) => next.append(key, v))
    } else {
      [...current, value].forEach((v) => next.append(key, v))
    }
    router.replace(`${pathname}?${next.toString()}`)
  }

  const clearFilters = () => router.replace(pathname)

  const FilterPanel = ({ isMobile }: { isMobile?: boolean }) => (
    <div className={`flex flex-col bg-white overflow-hidden ${isMobile ? 'h-full' : 'rounded-2xl border border-border shadow-sm h-[calc(100vh-140px)]'}`}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-[#fafafa]/50 shrink-0">
        <h2 className="text-xl font-bold tracking-tight">Filters</h2>
        {hasActiveFilters ? (
          <Button variant="secondary" size="sm" className="h-8 text-xs font-semibold bg-[#fcf2e3] text-[#8b5e34] hover:bg-[#fcf2e3]/80 gap-1.5" onClick={clearFilters}>
            <Trash2 className="h-3.5 w-3.5" /> Clear All
          </Button>
        ) : (
          <Button variant="ghost" size="sm" className="h-8 text-xs font-semibold text-muted-foreground gap-1.5 opacity-50 cursor-not-allowed">
            <Trash2 className="h-3.5 w-3.5" /> Clear All
          </Button>
        )}
      </div>

      <div className="overflow-y-auto flex-1 custom-scrollbar px-5">
        <Accordion type="multiple" defaultValue={['size', 'material', 'colour_or_finish']} className="pb-2">
          {[
            { key: 'size', label: 'Size', icon: Ruler, options: filterOptions.sizes },
            { key: 'material', label: 'Material', icon: Layers, options: filterOptions.materials },
            { key: 'colour_or_finish', label: 'Color / Finish', icon: Palette, options: [...new Set([...filterOptions.colours, ...filterOptions.finishes])] },
          ].map(({ key, label, icon: Icon, options }) =>
            options.length > 0 ? (
              <AccordionItem key={key} value={key} className="border-b border-border/60 last:border-0 transition-colors duration-200 data-[state=open]:border-brand-accent/60">
                <AccordionTrigger className="text-[15px] font-bold py-4 hover:no-underline hover:text-brand-accent transition-colors">
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5 text-[#8b5e34]" />
                    {label}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col gap-3 pb-4">
                    {options.map((opt) => {
                      const actualKey = key === 'colour_or_finish' 
                        ? (filterOptions.colours.includes(opt) ? 'colour' : 'finish') 
                        : key
                      
                      const isActive = (filters[actualKey as keyof ProductFilters] ?? []).includes(opt)
                      const count = filterCounts?.[opt] ?? 0
                      // if (count === 0 && !isActive) return null; // Hide options with 0 count unless active
                      
                      return (
                        <div key={opt} className="flex items-center justify-between group">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              id={`${actualKey}-${opt}`}
                              checked={isActive}
                              onCheckedChange={() => toggleFilter(actualKey as keyof ProductFilters, opt)}
                              className="rounded-[4px] border-border/60 data-[state=checked]:bg-brand-accent data-[state=checked]:border-brand-accent"
                            />
                            <Label htmlFor={`${actualKey}-${opt}`} className="text-[14px] font-medium cursor-pointer text-foreground/80 group-hover:text-foreground transition-colors flex items-center gap-2">
                              {key === 'colour_or_finish' && (
                                <span 
                                  className="w-4 h-4 rounded-full border border-black/10 shrink-0" 
                                  style={{ backgroundColor: REAL_COLOUR_MAP[opt] ?? '#e5e7eb' }} 
                                />
                              )}
                              {opt}
                            </Label>
                          </div>
                          <span className="text-[11px] font-semibold bg-[#fcf2e3] text-[#8b5e34] px-2 py-0.5 rounded-md min-w-[24px] text-center">
                            {count}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ) : null
          )}
        </Accordion>
      </div>

      <div className="px-5 py-4 bg-[#fafafa] border-t border-border flex items-center gap-3">
        <Tag className="w-5 h-5 text-[#8b5e34] shrink-0" />
        <span className="text-[14px] font-medium"><span className="font-bold text-foreground">{totalProducts}</span> products</span>
      </div>
    </div>
  )

  return (
    <>
      {/* Desktop Sidebar Content (just the filters part) */}
      <div className="hidden lg:block">
        <FilterPanel />
      </div>

      {/* Mobile filter trigger + active filter badges */}
      <div className="flex items-center justify-between mb-6 gap-4 lg:hidden">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">
            {`${totalProducts} product${totalProducts !== 1 ? 's' : ''}`}
          </span>
          {hasActiveFilters && (
            <>
              {Object.entries(filters).flatMap(([key, values]) =>
                (values ?? []).map((v: string) => (
                  <Badge
                    key={`${key}-${v}`}
                    variant="secondary"
                    className="text-xs gap-1 cursor-pointer"
                    onClick={() => toggleFilter(key as keyof ProductFilters, v)}
                  >
                    {v} <X className="h-2.5 w-2.5" />
                  </Badge>
                ))
              )}
              <button className="text-xs text-muted-foreground hover:text-foreground" onClick={clearFilters}>
                Clear all
              </button>
            </>
          )}
        </div>
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" size="sm" className="lg:hidden gap-2 text-xs">
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Filters
              {hasActiveFilters && (
                <Badge className="text-white text-xs h-4 px-1" style={{ backgroundColor: '#C79A5C' }}>
                  {Object.values(filters).reduce((n, v) => n + (v?.length ?? 0), 0)}
                </Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[85vw] max-w-[320px] p-0 flex flex-col">
            <SheetHeader className="sr-only">
              <SheetTitle>Filter Products</SheetTitle>
            </SheetHeader>
            <FilterPanel isMobile />
          </SheetContent>
        </Sheet>
      </div>
    </>
  )
}
